import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import { BarChart, colorAt } from "@outpave/ds";
import CustomSkeleton from "components/common/CustomSkeleton";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { useApiKeyMetrics, ApiKeyMetric } from "hooks/useApiKeys";

interface ApiKeyMetricsChartProps {
  apiKeyId: string;
  days?: number;
  // Optional callback fired with the total call count over the window, which
  // lets the parent table fill in the "30-day calls" column without a
  // second query.
  onTotalsChange?: (totals: { calls: number; errors: number }) => void;
}

interface DailyBucket {
  day: string;
  calls: number;
  errors: number;
  byRoute: Record<string, number>;
}

const CHART_HEIGHT = 240;

/** Label roughly one band a week, so a 30 day axis stays readable. */
const TICK_STRIDE = 7;

// Palette slots rather than the danger token the errors series wants: slots 1
// to 3 are the brand identity colours, so no fixed semantic colour stays
// distinct from them across tenants. The missing danger slot is recorded under
// "What the API access review left behind" in DS_MIGRATION.md.
const OK_COLOR = colorAt(undefined, 0);
const ERRORS_COLOR = colorAt(undefined, 1);

/**
 * Roll the hour-granularity metrics into per-day totals so the chart axis
 * stays readable across a 30-day window (30 bars instead of 720).
 *
 * Exported for its own test: the rollup and the ordering are this component's
 * only real logic, and neither is reachable through the rendered chart.
 */
export function aggregateByDay(metrics: ApiKeyMetric[]): DailyBucket[] {
  const map = new Map<string, DailyBucket>();
  for (const m of metrics) {
    const day = m.hourBucket.slice(0, 10); // "YYYY-MM-DD"
    const existing = map.get(day) || {
      day,
      calls: 0,
      errors: 0,
      byRoute: {} as Record<string, number>,
    };
    existing.calls += m.totalCalls || 0;
    existing.errors += m.totalErrors || 0;
    for (const r of m.byRoute || []) {
      existing.byRoute[r.route] = (existing.byRoute[r.route] || 0) + r.calls;
    }
    map.set(day, existing);
  }
  // Return in chronological order so the chart reads left-to-right.
  return Array.from(map.values()).sort((a, b) => a.day.localeCompare(b.day));
}

const ApiKeyMetricsChart: React.FC<ApiKeyMetricsChartProps> = ({
  apiKeyId,
  days = 30,
  onTotalsChange,
}) => {
  const { t, i18n } = useTranslation();
  const { metrics, loading, error } = useApiKeyMetrics(apiKeyId, days);

  // The band values are the axis labels, so they are formatted for the reader's
  // locale rather than dayjs's default English, as StatementsTable does.
  const dayFormat = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      }),
    [i18n.language],
  );

  const data = useMemo(() => aggregateByDay(metrics), [metrics]);

  const totals = useMemo(() => {
    const calls = metrics.reduce((sum, m) => sum + (m.totalCalls || 0), 0);
    const errors = metrics.reduce((sum, m) => sum + (m.totalErrors || 0), 0);
    return { calls, errors };
  }, [metrics]);

  // Fire the totals callback after render so the parent's table can show
  // the "30-day calls" column without round-tripping. useEffect to avoid
  // setState-during-render warnings.
  //
  // Only once the query has actually answered: metrics is empty while it is in
  // flight and after it fails, so publishing then puts a 0 in the table that is
  // not a measurement, and a failed request reads as a key nobody called. An
  // empty SUCCESSFUL window really is zero and still publishes.
  React.useEffect(() => {
    if (loading || error) return;
    if (onTotalsChange) onTotalsChange(totals);
  }, [totals, onTotalsChange, loading, error]);

  if (loading) {
    return (
      <Box sx={{ minHeight: CHART_HEIGHT }}>
        <CustomSkeleton variant="graph" />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" sx={{ color: "var(--color-text-danger)" }}>
        {t("ApiKeyUsageLoadError")}
      </Typography>
    );
  }

  if (!data.length) {
    return (
      <Inline justify="center" sx={{ paddingBlock: "var(--space-9)" }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {t("ApiKeyNoUsageInRange", { days })}
        </Typography>
      </Inline>
    );
  }

  return (
    <Stack gap="var(--space-4)">
      <Inline gap="var(--space-9)" align="start">
        <Stack gap="var(--space-1)">
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {t("Total calls")}
          </Typography>
          <Typography variant="h6">{totals.calls.toLocaleString()}</Typography>
        </Stack>
        <Stack gap="var(--space-1)">
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {t("Errors")}
          </Typography>
          <Typography variant="h6">{totals.errors.toLocaleString()}</Typography>
        </Stack>
      </Inline>
      <BarChart
        height={CHART_HEIGHT}
        grid={{ horizontal: true }}
        // The DS runs the plot to the card edge, which clips the last tick
        // label: the final labelled band sits two bars short of the right edge.
        margin={{ right: 20 }}
        xAxis={[
          {
            // Formatted into the band values rather than through the axis
            // valueFormatter, which a band axis does not reach: the raw ISO
            // days render long enough that the last tick ellipsizes. UTC
            // because the bucket is a UTC calendar day, not a local instant.
            data: data.map((bucket) =>
              dayFormat.format(new Date(`${bucket.day}T00:00:00Z`)),
            ),
            scaleType: "band",
            tickInterval: (_value, index) => index % TICK_STRIDE === 0,
          },
        ]}
        yAxis={[
          {
            min: 0,
            // Calls and errors are counts, and a key with a handful of them
            // gets a "nice" axis through 0.5. recharts had allowDecimals, and
            // BarAxisConfig exposes no tick step, so the fractional ticks lose
            // their labels instead (DS gap, recorded in DS_MIGRATION.md).
            valueFormatter: (value) =>
              Number.isInteger(value) ? value.toLocaleString() : "",
          },
        ]}
        series={[
          {
            // The server counts an errored request in BOTH totals, so stacking
            // calls under errors would draw 100 calls and 10 errors as a bar of
            // 110. The successful remainder is what stacks, and the bar height
            // is then the day's total calls.
            id: "ok",
            label: t("Successful"),
            stack: "usage",
            data: data.map((bucket) =>
              Math.max(bucket.calls - bucket.errors, 0),
            ),
            color: OK_COLOR,
            valueFormatter: (value) => value.toLocaleString(),
          },
          {
            id: "errors",
            label: t("Errors"),
            stack: "usage",
            data: data.map((bucket) => bucket.errors),
            color: ERRORS_COLOR,
            valueFormatter: (value) => value.toLocaleString(),
          },
        ]}
      />
    </Stack>
  );
};

export default ApiKeyMetricsChart;
